import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Grid,
  InputAdornment,
  Paper,
  SvgIcon,
  TextField
} from '@material-ui/core';
import { BranchListDropDown } from 'src/components/dropdown';
import { Search as SearchIcon } from 'react-feather';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { Autocomplete } from '@material-ui/lab';
import { isEmpty } from 'lodash';
import ClearIcon from '@material-ui/icons/Clear';
import GetAppIcon from '@material-ui/icons/GetApp';
import { usePermissions, useSnackBar } from 'src/hooks';
import { downloadCSV, snakeToNaturalCase, toTitleCase } from 'src/utils';
import { unwrapResult } from '@reduxjs/toolkit';
import { usePriceList } from 'src/hooks/priceList';
import { useLocation, useNavigate } from 'react-router';
import { objectToUrlSlug, parseSearchV2 } from 'src/utils';
import { queryParamsInterface } from '../constants';
import { FilterStockEnum } from 'src/enums/product/status';

const { actions: productActions } = slices.product;

const {
  actions: categoryActions,
  selectors: categorySelectors
} = slices.category;

interface ToolbarProps {
  isLoading?: boolean;
  isPoweredBuildsTable?: boolean;
  isIncomingPriceListTable?: boolean;
  currentTab?: number;

  onResetParams?: () => void;
}

const component = ({
  isLoading,
  isPoweredBuildsTable = false,
  isIncomingPriceListTable = false,
  currentTab = 0 // default tab is first tab index
}: ToolbarProps) => {
  const {
    canSeePriceListDealersPrice,
    canSeePriceListGrossPrice,
    canSeePriceListMarginPercentage,
    canDownloadPriceListCSV
  } = usePermissions();
  const snackbar = useSnackBar();

  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    params,
    poweredBuildsComponentData,
    setParams,
    getPriceList,
    setTabValue
  } = usePriceList();

  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const categories = useAppSelector(categorySelectors.selectCategories);

  const [searchName, setSearchName] = useState<string>('');
  const [branchIds, setBranchIds] = useState<number[]>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categLoading, setCategLoading] = useState<boolean>();
  const [resetBranchValue, setResetBranchValue] = useState(false);
  const [productFilter, setProductFilter] = useState<string>(
    FilterStockEnum.InStocks
  );

  // for getting value for payload
  const getIdFromName = (val: string, options: any[]) =>
    options.find((opt: any) => opt.name === val)?.id;

  const handleResetComplete = () => {
    setResetBranchValue(false);
  };

  const getCategories = useCallback(
    async (keyword?: string) => {
      setCategLoading(true);
      try {
        setCategLoading(true);
        await dispatch(categoryActions.getCategoriesThunk({ keyword }));
      } catch (error) {
        console.error(error);
      } finally {
        setCategLoading(false);
      }
    },
    [dispatch]
  );

  const applyFilter = () => {
    // const currentParams = parseSearchV2(search);
    let newQueryParams: queryParamsInterface = {
      // ...currentParams,
      ...(branchIds?.[0] && { branch_ids: branchIds }),
      ...(!!categoryId && { category_id: categoryId }),
      ...(searchName && { keyword: searchName }),
      ...(productFilter && { product_filter: productFilter }),
      tab: currentTab
    };

    const slug = objectToUrlSlug(newQueryParams);

    navigate({ search: String(slug) }, { replace: true });

    setParams(newQueryParams);
  };

  const onClickDownload = useCallback(async () => {
    if (!canDownloadPriceListCSV) {
      return;
    }

    const thunk = isPoweredBuildsTable
      ? productActions.getPoweredBuildsPriceListCSVThunk
      : productActions.getPriceListForCSVThunk;

    const fileName = isPoweredBuildsTable
      ? 'Powered_Build_Price_List.csv'
      : 'Price_List.csv';

    const res = unwrapResult(await dispatch(thunk())).originalData;

    if (res.success) {
      const formattedCSV = res.data?.map((data) => {
        let dataWithPermission = { ...data };
        if (!canSeePriceListDealersPrice) {
          delete dataWithPermission.dealers_price;
        }
        if (!canSeePriceListGrossPrice) {
          delete dataWithPermission.gross_price;
        }
        if (!canSeePriceListMarginPercentage) {
          delete dataWithPermission.margin_percentage;
        }
        const installmentColumns = Object.fromEntries(
          Object.entries(data.installment || {}).map(([key, value]) => [
            `${key} installment`,
            value
          ])
        );

        const stockColumns = Object.fromEntries(
          (data.stocks || []).map((stock) => [
            `${stock.branch_name} stocks`,
            stock.stocks
          ])
        );

        const transferStockColumns = Object.fromEntries(
          (data.transfer_stocks || []).map((stock) => [
            `${stock.branch_name} transfer stocks`,
            stock.stocks
          ])
        );

        // we dont need this anymore
        delete dataWithPermission.installment;
        delete dataWithPermission.stocks;
        delete dataWithPermission.transfer_stocks;

        return {
          ...dataWithPermission,
          ...installmentColumns,
          ...stockColumns,
          ...transferStockColumns
        };
      });

      downloadCSV(formattedCSV, fileName);
    } else {
      snackbar.show({
        message: 'Failed to download CSV',
        useSound: true,
        severity: 'error'
      });
    }
  }, [
    canDownloadPriceListCSV,
    canSeePriceListDealersPrice,
    canSeePriceListGrossPrice,
    canSeePriceListMarginPercentage,
    dispatch,
    isPoweredBuildsTable,
    snackbar
  ]);

  const onClickUpcomingDownload = useCallback(async () => {
    if (!canDownloadPriceListCSV) {
      return;
    }

    const thunk = productActions.getIncomingProductsListCSVThunk;

    const fileName = 'Incoming_Price_List.csv';

    const res = unwrapResult(await dispatch(thunk())).originalData;

    if (res.success) {
      const formattedCSV = res.data?.map((data) => {
        let dataWithPermission = { ...data };
        if (!canSeePriceListDealersPrice) {
          delete dataWithPermission.dealers_price;
        }
        if (!canSeePriceListGrossPrice) {
          delete dataWithPermission.gross_price;
        }
        if (!canSeePriceListMarginPercentage) {
          delete dataWithPermission.margin_percentage;
        }

        const installmentColumns = Object.fromEntries(
          Object.entries(data.installment || {}).map(([key, value]) => [
            `${key} installment`,
            value
          ])
        );

        const stockColumns = Object.fromEntries(
          (data.incoming_stocks || []).map((stock) => [
            `${stock.branch_name} stocks`,
            stock.incoming_stocks
          ])
        );

        // we dont need this anymore
        delete dataWithPermission.installment;
        delete dataWithPermission.incoming_stocks;

        return {
          ...dataWithPermission,
          ...installmentColumns,
          ...stockColumns
        };
      });

      downloadCSV(formattedCSV, fileName);
    } else {
      snackbar.show({
        message: 'Failed to download CSV',
        useSound: true,
        severity: 'error'
      });
    }
  }, [
    canDownloadPriceListCSV,
    canSeePriceListDealersPrice,
    canSeePriceListGrossPrice,
    canSeePriceListMarginPercentage,
    dispatch,
    snackbar
  ]);

  const onClear = () => {
    setSearchName('');

    const currentParams = parseSearchV2(search);
    delete currentParams.keyword;
  };

  const getOptionLabel = (val: string) => {
    return toTitleCase(snakeToNaturalCase(val));
  };

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  // Reset states for filters when switching tabs
  useEffect(() => {
    onClear(); // reset keyword
    setResetBranchValue(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPoweredBuildsTable]);

  useEffect(() => {
    if (poweredBuildsComponentData?.open) {
      return;
    }
    const payload = isEmpty(search) ? {} : parseSearchV2(search);
    getPriceList(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Render Params from url to ui
  useEffect(() => {
    const { branch_ids, keyword, category_id, tab } = params;

    setBranchIds(branch_ids || []);
    setSearchName(keyword || '');
    setCategoryId(category_id || null);
    setTabValue(+(tab ?? 0));
    setProductFilter(params?.product_filter ?? FilterStockEnum.InStocks);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <Paper style={{ padding: '1em' }}>
      <Grid
        container
        style={{ columnGap: '1em', rowGap: '1em', justifyContent: 'end' }}
      >
        <Grid item xs={12} lg={3}>
          <TextField
            fullWidth
            disabled={isLoading}
            value={searchName}
            inputRef={inputRef}
            onChange={(event) => setSearchName(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon fontSize="small" color="action">
                    <SearchIcon />
                  </SvgIcon>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" style={{ cursor: 'pointer' }}>
                  {searchName ? <ClearIcon onClick={() => onClear()} /> : null}
                </InputAdornment>
              )
            }}
            placeholder="Search Pricelist Product"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} lg={3}>
          <BranchListDropDown
            defaultValue={branchIds}
            onHandleBranchChange={(branchIds) => setBranchIds(branchIds || [])}
            multiple={true}
            resetValue={resetBranchValue}
            onResetComplete={handleResetComplete}
          />
        </Grid>
        {/* Categories */}
        {!isPoweredBuildsTable ? (
          <Grid item xs={12} lg={3}>
            <Autocomplete
              disabled={isPoweredBuildsTable}
              fullWidth
              clearOnBlur
              value={categories.find((cat) => cat.id === categoryId) || null}
              onInputChange={(e, newInputValue, reason) => {
                if (reason === 'clear') {
                  setCategoryId(0);
                  return;
                }
                const currentVal = getIdFromName(newInputValue, categories);
                setCategoryId(currentVal);
              }}
              options={categories}
              loading={categLoading}
              getOptionLabel={(option: any) => option?.name || ''}
              renderInput={(params: any) => (
                <TextField {...params} label="Category" variant="outlined" />
              )}
            />
          </Grid>
        ) : null}

        {!isPoweredBuildsTable && !isIncomingPriceListTable ? (
          <Grid
            item
            xs={12}
            lg
            // lg={2}
          >
            <Autocomplete
              options={Object.values(FilterStockEnum)}
              value={productFilter}
              fullWidth
              getOptionLabel={getOptionLabel}
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  label="Product Filters"
                  variant="outlined"
                />
              )}
              onChange={(_, val) =>
                setProductFilter(val ?? FilterStockEnum.All)
              }
            />
          </Grid>
        ) : null}

        <Grid item xs={12} lg={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            style={{ height: '100%' }}
            onClick={applyFilter}
          >
            Apply Filters
          </Button>
        </Grid>

        {canDownloadPriceListCSV ? (
          <Grid item xs={12} lg={2}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ height: '100%' }}
              onClick={() =>
                isIncomingPriceListTable
                  ? onClickUpcomingDownload()
                  : onClickDownload()
              }
            >
              Export CSV
            </Button>
          </Grid>
        ) : null}
      </Grid>
    </Paper>
  );
};

export const PriceListToolbar = memo(component);
